import React, { useCallback, useContext, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import { UserContext } from 'contexts/UserContext';
import { useCancelPartnerOrderMutation } from 'shared/hooks/fbm/useCancelPartnerOrderMutation';

function CancelFBMPartnerOrderModal({ isOpen, onClose, onError, onSuccess, orderNum }) {
    const [cancelReason, setCancelReason] = useState('');
    const { fbmAccount } = useContext(UserContext);

    const handleClose = useCallback(() => {
        setCancelReason('');
        onClose();
    }, [onClose]);

    const { isPending, mutate: cancelPartnerOrder } = useCancelPartnerOrderMutation({
        onError: (error) => {
            onError(error.message || 'Failed to cancel partner order');
        },
        onSuccess: () => {
            onSuccess(`Partner order ${orderNum} cancelled successfully`);
            handleClose();
        },
    });

    const handleCancelPartnerOrder = useCallback(() => {
        if (!cancelReason.trim()) {
            onError('Please provide a reason for cancellation');
            return;
        }

        cancelPartnerOrder({
            fbmAccountName: fbmAccount.fbmAccountName,
            orderNum,
            reason: cancelReason.trim(),
        });
    }, [cancelPartnerOrder, cancelReason, fbmAccount.fbmAccountName, onError, orderNum]);

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth disableScrollLock hideBackdrop={false}>
            <DialogTitle>Cancel Partner Order</DialogTitle>
            <DialogContent>
                {orderNum && (
                    <div style={{ marginBottom: '20px' }}>
                        <Typography variant="subtitle1">
                            <strong>Partner Order Number:</strong> {orderNum}
                        </Typography>
                    </div>
                )}
                <Typography variant="body1" style={{ marginBottom: '10px' }}>
                    Please provide a reason for cancellation:
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    id="cancelPartnerOrderReason"
                    label="Cancellation Reason"
                    type="text"
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={cancelReason}
                    onChange={(event) => setCancelReason(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Close
                </Button>
                <Button
                    onClick={handleCancelPartnerOrder}
                    color="primary"
                    variant="contained"
                    disabled={isPending || !cancelReason.trim()}
                >
                    {isPending ? 'Cancelling...' : 'Confirm Cancellation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export { CancelFBMPartnerOrderModal };
